/* NFC Solutions — Offline Authentication client
 * -----------------------------------------------------------------
 * Thin wrapper around /api/auth/offline/* that keeps the App.js
 * login flow clean. Responsibilities:
 *
 *  • enrollOfflineCredentials — call POST /auth/offline/enroll with
 *    the just-verified PIN, store the returned package in IDB.
 *  • refreshOfflineCredentials — silent refresh when connectivity
 *    returns; rotates verifier + updates last_refresh_at.
 *  • provisionOfflineSession — build a session-shaped object from a
 *    stored offline package after successful local PIN verification.
 *  • pingConnectivity — cheap health probe with short timeout.
 */

import axios from "axios";
import bcrypt from "bcryptjs";
import { getDeviceId } from "./device";
import {
  storeOfflineCredentials,
  getOfflineCredentials,
  clearOfflineCredentials,
  verifyPinOffline,
  readOne,
  getBusiness,
} from "./localDb";

const BACKEND_URL = (
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) || ""
);
const API = `${BACKEND_URL}/api`;

/** Ping the backend `/api/health` (or `/api/sync/business-lookup`) with
 * a short timeout so we don't hang the UI while deciding online vs
 * offline. Uses navigator.onLine as a fast-path negative. */
export async function pingConnectivity({ timeoutMs = 2500 } = {}) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return false;
  try {
    const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
    // GET /api/ (or any lightweight endpoint) — we don't care about the
    // shape, only the fact that we got any response back from a server.
    const res = await fetch(`${API}/`, {
      method: "GET",
      cache: "no-store",
      signal: ctrl ? ctrl.signal : undefined,
    }).catch(() => null);
    if (timer) clearTimeout(timer);
    if (!res) return false;
    // Session 27.17 — CRITICAL FIX: previously we treated ANY 5xx as
    // offline (`res.status < 500`). That caused Cloudflare 520/521/522
    // errors (which happen intermittently when the origin has a bad
    // moment) to flip the app into "offline" mode, blocking real API
    // traffic even though the network was perfectly healthy.
    //
    // The correct semantic: "connectivity" means "there IS a server we
    // can reach." Any HTTP response — including a 5xx — proves that.
    // Only a null response (network error, DNS failure, timeout,
    // TypeError from fetch) means we're truly offline.
    return true;
  } catch (_) { return false; }
}

/** After a successful ONLINE PIN login, enroll offline credentials so
 * the same user can sign in offline next time. Non-fatal — logs and
 * returns null on failure so the primary login flow is never blocked. */
export async function enrollOfflineCredentials({ token, pin, deviceLabel }) {
  if (!token || !pin) return null;
  let deviceId = "";
  try { deviceId = getDeviceId(); } catch (_) { deviceId = ""; }
  if (!deviceId) return null;
  try {
    const res = await axios.post(
      `${API}/auth/offline/enroll`,
      { pin, device_id: deviceId, device_label: deviceLabel || null },
      { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 },
    );
    const pkg = res.data;
    if (pkg && pkg.user_id) {
      await storeOfflineCredentials(pkg.user_id, pkg);
      return pkg;
    }
    return null;
  } catch (err) {
    // Enrollment failures shouldn't block the user — they'll just have
    // to sign in online next time until enrollment succeeds. Log for
    // diagnostics.
    console.warn("[offline-auth] enrollment failed:", err?.message || err);
    return null;
  }
}

/** Exchange a stored offline token for a fresh online session token.
 * Called silently when connectivity is confirmed. Returns the fresh
 * session payload on success, or null on any failure (including "token
 * was revoked" — in which case the caller should clear local creds). */
export async function refreshOfflineSession(userId) {
  const pkg = await getOfflineCredentials(userId);
  if (!pkg || !pkg.offline_token) return null;
  let deviceId = "";
  try { deviceId = getDeviceId(); } catch (_) { deviceId = ""; }
  if (!deviceId) return null;
  try {
    const res = await axios.post(
      `${API}/auth/offline/refresh`,
      { offline_token: pkg.offline_token, device_id: deviceId },
      { timeout: 5000 },
    );
    return res.data || null;
  } catch (err) {
    // 401 → token was revoked / user deactivated / PIN changed. Purge
    // local creds so the login screen forces a fresh online login.
    const status = err?.response?.status;
    if (status === 401) {
      try { await clearOfflineCredentials(userId); } catch (_) { /* noop */ }
    }
    return null;
  }
}

/**
 * Attempt an OFFLINE sign-in. Verifies the PIN against the local
 * PBKDF2 verifier and, on success, returns a session-shaped payload
 * that the caller can hand to `setSession(role, payload)`.
 *
 * The returned session carries `mode: "offline"` so downstream code
 * can decide what to do with mutation attempts (queued in Session 26).
 *
 * @param {object} args
 * @param {object} args.user         — user object from local IDB roster
 * @param {string} args.pin          — PIN entered by the operator
 * @param {string} args.role         — session scope (staff/owner/kitchen/bar)
 * @param {object} args.business     — business object from local IDB
 * @returns {object|null} session payload on success, null on wrong PIN
 */
export async function verifyOfflineLogin({ user, pin, role, business }) {
  if (!user || !user.id || !pin) return null;
  const pkg = await verifyPinOffline(user.id, pin);
  if (!pkg) return null;
  // Mint a client-side token — prefixed so any place that accidentally
  // treats it as a bearer token gets an immediate 401 from the server.
  // Session 26 will introduce proper offline-session semantics.
  const token = `offline:${pkg.offline_token}`;
  const fullName = (
    ((user.first_name || "") + " " + (user.last_name || "")).trim()
    || user.full_name
    || user.username
    || ""
  );
  return {
    token,
    role,
    business: business || null,
    mode: "offline",
    offline_token: pkg.offline_token,
    user: {
      id: user.id,
      full_name: fullName,
      role: user.role,
      allowed_sections: user.allowed_sections || null,
    },
    // No server-side expiry when offline — refreshed by next
    // online contact via refreshOfflineSession().
    expires_in_hours: null,
  };
}


// ─────────────────────────────────────────────────────────────────────
// Session 27 — Local-first PIN verification via snapshot bcrypt hashes.
// ─────────────────────────────────────────────────────────────────────
// Rationale:
//   The `/sync/snapshot` endpoint now ships `users[].pin_hash` (bcrypt)
//   so the device can verify any staff PIN offline. This is the missing
//   piece for "true offline startup" — a freshly-installed device with
//   the snapshot but zero prior online logins can still sign anyone in.
//
//   Revocation happens naturally: when a user is deactivated or their
//   PIN changes, the next background snapshot refresh overwrites the
//   local `users` row and the stale hash disappears.
//
//   The bcryptjs verify runs in ~50-200ms on typical Android WebViews,
//   which is imperceptible in the login flow.

/**
 * Look up a user (by id) from the local snapshot and verify their PIN
 * against the locally stored bcrypt `pin_hash`.
 *
 * Returns:
 *   • the user record on match
 *   • null if the user is not in the local snapshot, is inactive, or
 *     the PIN doesn't match
 */
export async function verifyLocalPin(userId, pin) {
  if (!userId || !pin) return null;
  let userRec = null;
  try {
    userRec = await readOne("users", String(userId));
  } catch (_) { userRec = null; }
  if (!userRec) return null;
  // Inactive users lose offline access immediately upon next sync;
  // enforce it here too as a belt-and-braces check.
  if (userRec.active === false) return null;
  const hash = userRec.pin_hash;
  if (!hash || typeof hash !== "string") return null;
  try {
    // bcryptjs supports both $2a$ and $2b$ hashes used by passlib.
    const ok = await bcrypt.compare(String(pin), hash);
    if (!ok) return null;
    return userRec;
  } catch (_) { return null; }
}

/**
 * Full local-first offline login. Verifies the PIN against the local
 * snapshot's bcrypt hash and returns a session-shaped payload the
 * caller can hand to `setSession(role)`.
 *
 * Falls through to null if the user is unknown to the device or the
 * PIN is wrong — the caller can then try online login.
 */
export async function loginFromLocalSnapshot({ userId, pin, role }) {
  const user = await verifyLocalPin(userId, pin);
  if (!user) return null;
  const business = await getBusiness();
  const fullName = (
    ((user.first_name || "") + " " + (user.last_name || "")).trim()
    || user.full_name
    || user.username
    || ""
  );
  // We reuse the "offline:" token prefix so any accidental use of this
  // token as a bearer against the server gets an immediate 401 rather
  // than silently succeeding. This session is purely a client-side
  // credential and the sync engine attaches its own bearer token when
  // reaching out to the cloud.
  const localTok = `offline:local-${user.id}-${Date.now().toString(36)}`;
  return {
    token: localTok,
    role,
    business: business || null,
    mode: "offline",
    offline_token: localTok,
    user: {
      id: user.id,
      full_name: fullName,
      role: user.role,
      allowed_sections: user.allowed_sections || null,
    },
    expires_in_hours: null,
  };
}
